import { useEffect, useState, useCallback } from 'react';
import { useSocket } from '../hooks/useSocket.jsx';

/**
 * Listens for leave:status_changed events and shows a toast.
 * Mounted globally inside SocketProvider so any logged-in user can receive it.
 */
export function LeaveToastListener() {
    const { subscribe } = useSocket();
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    useEffect(() => {
        const off = subscribe('leave:status_changed', (e) => {
            const id = `${e.request_id}-${Date.now()}`;
            const tone = e.status === 'approved' ? 'approved' : 'rejected';
            const message = e.status === 'approved'
                ? `Your leave request was approved`
                : `Your leave request was rejected`;
            const detail = formatRange(e.start_date, e.end_date) + ' · ' + capitalise(e.leave_type);
            const reviewNote = e.review_note || null;

            setToasts((prev) => [...prev, { id, tone, message, detail, reviewNote }]);

            const timeoutMs = e.status === 'rejected' && reviewNote ? 12000 : 8000;
            setTimeout(() => dismiss(id), timeoutMs);
        });
        return off;
    }, [subscribe, dismiss]);

    if (toasts.length === 0) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 50,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
                maxWidth: 380,
            }}
        >
            {toasts.map((t) => {
                const accent = t.tone === 'approved'
                    ? 'var(--color-status-complete)'
                    : 'var(--color-danger)';
                const borderColor = t.tone === 'approved'
                    ? 'rgba(29, 158, 117, 0.35)'
                    : 'var(--color-danger-border)';
                return (
                    <div
                        key={t.id}
                        role="alert"
                        style={{
                            background: 'var(--color-bg-primary)',
                            borderRadius: 'var(--border-radius-lg)',
                            boxShadow: '0 10px 25px -8px rgba(0,0,0,0.18), 0 4px 8px -4px rgba(0,0,0,0.12)',
                            border: `0.5px solid ${borderColor}`,
                            padding: 14,
                        }}
                    >
                        <div className="flex items-start" style={{ gap: 10 }}>
                            <span
                                style={{
                                    marginTop: 4,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: accent,
                                    flexShrink: 0,
                                }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{t.message}</p>
                                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                    {t.detail}
                                </p>
                                {t.reviewNote && (
                                    <p
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--color-text-secondary)',
                                            marginTop: 8,
                                            fontStyle: 'italic',
                                            background: 'var(--color-bg-secondary)',
                                            padding: '6px 10px',
                                            borderRadius: 'var(--border-radius-md)',
                                        }}
                                    >
                                        “{t.reviewNote}”
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => dismiss(t.id)}
                                aria-label="Dismiss"
                                style={{
                                    color: 'var(--color-text-tertiary)',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: 11,
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    padding: 4,
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function formatRange(start, end) {
    if (start === end) return formatDate(start);
    return `${formatDate(start)} – ${formatDate(end)}`;
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function capitalise(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}
